import { isArray, memoize } from 'lodash';
import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { BrowserRouter as Router } from 'react-router-dom';

import type { Props } from '@swf/components/Translation/Translation';
import Translation from '@swf/components/Translation/Translation';
import type { Store } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { TrackedTranslation, TranslationTag, Values } from '@swf/core/types/i18n';
import { canUseDOM } from '@swf/core/utils';
import type { ValidatorTranslation } from '@swf/core/utils/validators';

import Providers from '../../swf-app/Providers';

interface TrackingProps extends Props, TrackedTranslation {}

export const toLocaleDateString = (language: string, dateTime: string) => {
  const convertedFromIsoString = dateTime ? new Date(dateTime) : new Date();
  const timeOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };

  return convertedFromIsoString.toLocaleDateString(language, timeOptions);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isTranslation = (value?: any): value is TranslationTag =>
  !!value?.id && !!value?.defaultMessage;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const validateTranslation = (value?: any): value is TranslationTag | TranslationTag[] =>
  isArray(value) ? value.every(isTranslation) : isTranslation(value);

const valuesKey = (values: Values = {}) =>
  Object.values(values)
    .filter((item) => typeof item !== 'object')
    .join('');

export const trackingTranslation = memoize(
  (
    translation: string | TrackingProps | TrackingProps[] | undefined | React.ReactElement,
    store: Store,
    values?: Values
  ): string | undefined => {
    if (typeof translation === 'string') return translation;
    if (!translation || !canUseDOM || !validateTranslation(translation)) return;
    const elem = document.createElement('div');
    const state = store.getState();
    const translations: TrackingProps[] = isArray(translation) ? translation : [translation];
    const locale = select.locale.get(state);

    if (process.env.NODE_ENV === 'test') {
      return translations[0].id;
    }

    try {
      elem.innerHTML = renderToStaticMarkup(
        <Router basename={select.location.getBaseURL(state)}>
          <Providers store={store}>
            {translations.reduce<React.ReactNode[]>(
              (memo, { track, ...item }: TrackedTranslation | ValidatorTranslation, index) => {
                memo.push(
                  <Translation
                    key={item.id}
                    values={values}
                    {...item}
                    defaultMessage={locale === 'en' ? item.defaultMessage : track}
                  />
                );
                if (index < translations.length - 1) memo.push(' ');
                return memo;
              },
              []
            )}
          </Providers>
        </Router>
      );
      return elem.innerText;
    } catch (err) {
      return undefined;
    }
  },
  (translation, store, values) => {
    if (typeof translation === 'string') return translation;
    if (!translation || !validateTranslation(translation)) return;
    const translations: TrackingProps[] = isArray(translation) ? translation : [translation];
    return (
      translations
        .map(
          ({ id, defaultMessage, values: inlineValues }) =>
            id + defaultMessage + valuesKey(inlineValues)
        )
        .join('') + valuesKey(values)
    );
  }
);

export const mapDatePickerLocale = (locale: unknown) => {
  switch (locale) {
    case 'es-419':
      return 'es';
    case 'pt-BR':
      return 'pt';
    default:
      return locale;
  }
};
